import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/modules/auth/useAuth';
import { getSupabase } from '@/lib/supabase';
import { subscribeTable } from '@/lib/realtime';
import type { HottrMessageRow } from '@/lib/database.types';
import {
  mapMessageRow as mapRow,
  validateMessageContent,
  type HottrMessage,
} from './message';

export type { HottrMessage } from './message';

export interface MessagesState {
  messages: HottrMessage[];
  loading: boolean;
  sending: boolean;
  sendMessage(content: string): Promise<{ ok: boolean; error?: string }>;
}

export const useMessages = (conversationId: string | null): MessagesState => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<HottrMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!conversationId || !user) return;
    const supabase = getSupabase();
    if (!supabase) return;

    setLoading(true);
    let cancelled = false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabase as any;

    const load = async () => {
      const { data } = await supabase
        .from('hottr_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (cancelled) return;
      setMessages((data ?? []).map(mapRow));
      setLoading(false);

      db
        .from('hottr_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('conversation_id', conversationId)
        .neq('sender_id', user.id)
        .is('read_at', null)
        .then(() => {});
    };

    load();

    const unsubscribe = subscribeTable(
      supabase,
      `hottr-messages-${conversationId}`,
      {
        event: 'INSERT',
        table: 'hottr_messages',
        filter: `conversation_id=eq.${conversationId}`,
      },
      (payload) => {
        if (!payload?.new) return;
        setMessages((prev) => [...prev, mapRow(payload.new as HottrMessageRow)]);
        if ((payload.new as HottrMessageRow).sender_id !== user.id) {
          db
            .from('hottr_messages')
            .update({ read_at: new Date().toISOString() })
            .eq('id', (payload.new as HottrMessageRow).id)
            .then(() => {});
        }
      },
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [conversationId, user?.id]);

  const sendMessage = useCallback(
    async (content: string): Promise<{ ok: boolean; error?: string }> => {
      if (!conversationId || !user) return { ok: false, error: 'Not in a conversation.' };
      const supabase = getSupabase();
      if (!supabase) return { ok: false, error: 'No connection.' };

      const validated = validateMessageContent(content);
      if (!validated.ok) return { ok: false, error: validated.error };

      setSending(true);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const db2 = supabase as any;
      const { error } = await db2.from('hottr_messages').insert({
        conversation_id: conversationId,
        sender_id: user.id,
        content: validated.content,
      }) as { error: { message: string } | null };

      if (!error) {
        db2
          .from('hottr_conversations')
          .update({ last_message_at: new Date().toISOString() })
          .eq('id', conversationId)
          .then(() => {});
      }

      setSending(false);
      return error ? { ok: false, error: error.message } : { ok: true };
    },
    [conversationId, user?.id],
  );

  return { messages, loading, sending, sendMessage };
};
